import { useState } from 'react';
import CardStack3D, { type Plan } from '../components/CardStack3D';
import { subscriptionPlans } from '../data/subscriptionPlans';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { gradientPillCss } from '../styles/gradientPill';
import { SERVICE_COPY_BY_CATEGORY, type ServiceCopyCategory } from '../content/serviceCopy';
import ServiceOfferingsCarousel from '../components/services/ServiceOfferingsCarousel';

const serviceStackPlans: Plan[] = subscriptionPlans.map((plan) => {
  switch (plan.id) {
    case 'marketing':
      return {
        ...plan,
        name: 'Managed Marketing',
        animationKey: 'digital',
        description: 'SEO, paid media, social campaigns, and ongoing performance reporting.',
      };
    case 'digital-services':
      return {
        ...plan,
        name: 'Assets & Design',
        animationKey: 'creative',
        animationOffsetY: '14%',
        description: 'Brand assets, landing pages, creative systems, and design execution.',
      };
    case 'ecommerce-digital-services':
      return {
        ...plan,
        name: 'Platform Development',
        animationKey: 'marketing',
        description: 'Custom websites, ecommerce builds, integrations, and internal platforms.',
      };
    default:
      return plan;
  }
});

function resolveServiceCopyCategory(planId: string): ServiceCopyCategory | null {
  switch (planId) {
    case 'marketing':
      return 'marketing';
    case 'digital-services':
      return 'assets_design';
    case 'ecommerce-digital-services':
      return 'platform';
    default:
      return null;
  }
}

const Section = styled.section`
  position: relative;
  overflow: hidden;
  padding: 96px 0 72px;
  background: #efecf0;
`;

const Inner = styled.div`
  width: min(1380px, calc(100% - 2rem));
  margin: 0 auto;
  display: grid;
  gap: 2rem;
`;

const Header = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 1rem;
  align-items: end;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
    justify-items: center;
    text-align: center;
  }
`;

const HeadingBlock = styled.div`
  display: grid;
  gap: 0.55rem;

  @media (max-width: 760px) {
    justify-items: center;
  }
`;

const Eyebrow = styled.span`
  display: inline-flex;
  width: fit-content;
  align-items: center;
  padding: 0.35rem 0.72rem;
  border-radius: 999px;
  
  color: #243447;
  font-size: 0.74rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const Title = styled.h2`
  margin: 0;
  font-size: clamp(2.35rem, 5vw, 4rem);
  line-height: 0.96;
  letter-spacing: -0.03em;
  color: #17212f;

  span {
    font-family: var(--font-serif);
    font-style: italic;
    font-weight: 600;
  }
`;

const Lead = styled.p`
  margin: 0;
  max-width: 62ch;
  color: #465263;
  font-size: clamp(1rem, 1.9vw, 1.08rem);
  line-height: 1.6;
`;

const SectionLink = styled(Link)`
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  min-height: 48px;
  padding: 0.72rem 1rem;
  border-radius: 999px;
  border: 1px solid rgba(23, 33, 47, 0.14);
  background: rgba(255, 255, 255, 0.78);
  color: #17212f;
  font-size: 0.9rem;
  font-weight: 800;
  box-shadow: 0 10px 24px rgba(17, 24, 39, 0.06);
  transition: transform 160ms ease, box-shadow 160ms ease, background 160ms ease;

  &:hover {
    transform: translateY(-2px);
    background: #ffffff;
    box-shadow: 0 14px 28px rgba(17, 24, 39, 0.1);
  }
`;

const PlansShelf = styled.div`
  width: min(1180px, 100%);
  margin: 0 auto;
  padding-top: 0.2rem;
`;

const PlansLayout = styled.div`
  display: grid;
  gap: clamp(2rem, 4vw, 4.5rem);
  align-items: center;

  @media (min-width: 980px) {
    grid-template-columns: minmax(320px, 0.9fr) minmax(0, 1fr);
  }
`;

const PlansStackWrap = styled.div`
  min-width: 0;
`;

const PlansStack = styled(CardStack3D)`
  width: 100%;
`;

const PlansCopy = styled.div`
  display: grid;
  gap: 1.15rem;
  align-content: center;
  color: #17212f;

  @media (max-width: 979px) {
    text-align: center;
  }
`;

const PlansCopyEyebrow = styled.span`
  font-size: 0.74rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(23, 33, 47, 0.56);
`;

const PlansCopyTitle = styled.h3`
  margin: 0;
  font-size: clamp(2rem, 4vw, 3.2rem);
  line-height: 0.96;
  letter-spacing: -0.04em;
`;

const PlansCopyLead = styled.p`
  margin: 0;
  max-width: 56ch;
  color: #465263;
  font-size: 1rem;
  line-height: 1.65;

  @media (max-width: 979px) {
    max-width: none;
  }
`;

const PlansTextList = styled.div`
  display: grid;
  gap: 1rem;
`;

const PlansCta = styled(Link)`
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  margin-top: 0.35rem;
  ${gradientPillCss};

  @media (max-width: 979px) {
    justify-self: center;
  }
`;

const PlansTextItem = styled.div`
  display: grid;
  gap: 0.2rem;
`;

const PlansTextTitle = styled.h4`
  margin: 0;
  font-size: 1.05rem;
  font-weight: 800;
  letter-spacing: -0.02em;
`;

const PlansTextBody = styled.p`
  margin: 0;
  color: #465263;
  font-size: 0.96rem;
  line-height: 1.6;
`;

export default function ServicesSection() {
  const [activeServiceCategory, setActiveServiceCategory] = useState<ServiceCopyCategory>('marketing');
  const activeServiceCopy = SERVICE_COPY_BY_CATEGORY[activeServiceCategory];

  return (
    <Section id="services">
      <Inner>
        <Header>
          <HeadingBlock>
            <Eyebrow>Service Tracks</Eyebrow>
            <Title>
              Our <span>Services</span>
            </Title>
            <Lead>
              Search-focused digital offerings across development, design, integrations, marketing,
              and optimization.
            </Lead>
          </HeadingBlock>

          <SectionLink to="/services">View full service menu</SectionLink>
        </Header>

        <ServiceOfferingsCarousel />

        <PlansShelf>
          <PlansLayout>
            <PlansStackWrap>
              <PlansStack
                plans={serviceStackPlans}
                initialIndex={0}
                hidePrice
                centerOnMobile
                onChange={(plan) => {
                  const nextCategory = resolveServiceCopyCategory(plan.id);

                  if (nextCategory) {
                    setActiveServiceCategory(nextCategory);
                  }
                }}
              />
            </PlansStackWrap>

            <PlansCopy>
              <PlansCopyEyebrow>{activeServiceCopy.eyebrow}</PlansCopyEyebrow>
              <PlansCopyTitle>{activeServiceCopy.title}</PlansCopyTitle>
              <PlansCopyLead>
                {activeServiceCopy.lead}
              </PlansCopyLead>

              <PlansTextList>
                {activeServiceCopy.points.map((point) => (
                  <PlansTextItem key={point.title}>
                    <PlansTextTitle>{point.title}</PlansTextTitle>
                    <PlansTextBody>{point.body}</PlansTextBody>
                  </PlansTextItem>
                ))}
              </PlansTextList>

              <PlansCta to={activeServiceCopy.ctaTo}>
                {activeServiceCopy.ctaLabel}
              </PlansCta>
            </PlansCopy>
          </PlansLayout>
        </PlansShelf>
      </Inner>
    </Section>
  );
}
